import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X, AlertCircle, CheckCircle } from "lucide-react";
import { v4 as uuid } from "uuid";
import { progressService } from "../../../../../services/progressService";
import pandaAvatar from "../../../../../assets/Panda_headshot.png";

import Lion from "../../../../../assets/animals/Lion.png";
import Monkey from "../../../../../assets/animals/Monkey.png";
import Tiger from "../../../../../assets/animals/Tiger.png";
import Koala from "../../../../../assets/animals/Koala.png";
import Fox from "../../../../../assets/animals/Fox.png";
import Panda from "../../../../../assets/animals/Piglet.png";
import Cat from "../../../../../assets/animals/Cat.png";
import Gorilla from "../../../../../assets/animals/Gorilla.png";
import Hyena from "../../../../../assets/animals/Hyena.png";

const defaultRelationOptions = [
  "Boss (Direct Manager)",
  "Skip-Level / Super Boss",
  "Business / Functional Head",
  "Project / Program Sponsor",
  "Client Sponsor/ Key Customer",
  "Peer Team Boss",
  "PMO / Portfolio Lead",
  "Product / Sec. Leaders",
  "Boss's Peer",
  "HRBP / Finance Partner",
  "Other",
];

const animalIcons = [Lion, Monkey, Tiger, Koala, Fox, Panda, Cat, Gorilla, Hyena];
const createStakeholder = (index) => ({
  id: uuid(),
  name: "",
  relation: "",
  customRelation: "",
  animal: animalIcons[index % animalIcons.length],
});

/* ---------- Toast Notification Component ---------- */
const Toast = ({ message, type = "info", onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const getIcon = () => {
    switch (type) {
      case "success":
        return <CheckCircle className="w-5 h-5 text-[#2E8B87] flex-shrink-0" />;
      case "error":
        return <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />;
      case "warning":
        return <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />;
      default:
        return <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />;
    }
  };

  const getBgColor = () => {
    switch (type) {
      case "success":
        return "bg-green-50 border-green-200";
      case "error":
        return "bg-red-50 border-red-200";
      case "warning":
        return "bg-orange-50 border-orange-200";
      default:
        return "bg-blue-50 border-blue-200";
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20, x: 50 }}
      animate={{ opacity: 1, y: 0, x: 0 }}
      exit={{ opacity: 0, x: 50 }}
      transition={{ duration: 0.3 }}
      className={`fixed top-6 right-6 z-50 flex items-start gap-3 px-4 py-3 rounded-lg border shadow-lg max-w-md ${getBgColor()}`}
    >
      {getIcon()}
      <p className="text-sm text-gray-800 flex-1 font-medium">{message}</p>
      <button
        onClick={onClose}
        className="text-gray-500 hover:text-gray-700 transition"
      >
        <X className="w-4 h-4" />
      </button>
    </motion.div>
  );
};

const StakeholderBuilder = ({
  userId,
  subsectionId,
  onNextStep,
  onPreviousStep,
  onDataChange,
}) => {
  const [relationOptions, setRelationOptions] = useState(defaultRelationOptions);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const defaultStakeholderList = () =>
    Array.from({ length: 5 }, (_, i) => createStakeholder(i));

  const [stakeholders, setStakeholders] = useState(defaultStakeholderList);

  useEffect(() => {
    let isMounted = true;

    const loadStoredStakeholders = async () => {
      if (!userId) return;

      setStakeholders(defaultStakeholderList());

      try {
        const res = await progressService.getUserDataForSection(
          userId,
          "course_career_progression",
          "interpersonal",
          "interpersonal_list"
        );

        const list = Array.isArray(res?.stakeholders) ? res.stakeholders : [];
        const valid = list.filter((s) => s.name && s.relation);

        if (isMounted && valid.length) {
          setStakeholders(
            valid.map((s, i) => ({
              ...s,
              animal: s.animal || animalIcons[i % animalIcons.length],
            }))
          );
        }
      } catch (err) {
        console.error("Failed to load stakeholder builder data:", err);
      }
    };

    loadStoredStakeholders();

    return () => {
      isMounted = false;
    };
  }, [userId]);

  useEffect(() => {
    const customRelations = stakeholders
      .map((s) => s.relation)
      .filter(
        (r) => r && !defaultRelationOptions.includes(r) && r !== "Other"
      );

    if (customRelations.length > 0) {
      setRelationOptions((prev) => {
        const unique = [...new Set([...prev, ...customRelations])];
        return unique;
      });
    }
  }, [stakeholders]);

  const showToast = (message, type = "info") => {
    setToast({ message, type });
  };

  const handleChange = (i, field, value) => {
    const updated = [...stakeholders];
    updated[i][field] = value;
    if (field === "relation" && value !== "Other") {
      updated[i].customRelation = "";
    }
    setStakeholders(updated);
  };

  const handleCustomRelationKey = (i, e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const value = stakeholders[i].customRelation.trim();
      if (!value) return;

      if (!relationOptions.includes(value)) {
        setRelationOptions((prev) => [
          ...prev.filter((opt) => opt !== "Other"),
          value,
          "Other",
        ]);
      }

      const updated = [...stakeholders];
      updated[i].relation = value;
      updated[i].customRelation = "";
      setStakeholders(updated);
    }
  };

  const handleCustomRelationChange = (i, value) => {
    const updated = [...stakeholders];
    updated[i].customRelation = value;
    setStakeholders(updated);
  };

  const addStakeholder = () =>
    setStakeholders([...stakeholders, createStakeholder(stakeholders.length)]);

  const deleteStakeholder = (id) =>
    setStakeholders(stakeholders.filter((s) => s.id !== id));

  const validCount = stakeholders.filter((s) => s.name && s.relation).length;
  const bossCount = stakeholders.filter(
    (s) => s.name && s.relation === "Boss (Direct Manager)"
  ).length;

  const handleNext = async () => {
    if (bossCount === 0) {
      showToast("Please select your direct manager as a stakeholder.", "error");
      return;
    }

    if (bossCount > 1) {
      showToast("Please select only one 'Boss (Direct Manager)' stakeholder", "error");
      return;
    }

    try {
      setIsSaving(true);
      onDataChange?.({ stakeholders });

      const validStakeholders = stakeholders.filter(
        (s) => s.name && s.relation
      );
      if (validStakeholders.length < 1) {
        console.warn("No valid stakeholders to save; skipping API call.");
        onNextStep?.();
        return;
      }

      await progressService.saveProgress(
        userId,
        subsectionId,
        { stakeholders },
        "completed"
      );

      onNextStep?.();
    } catch (error) {
      console.error("Error saving stakeholder data:", error);
      showToast("Error saving stakeholder data. Please try again.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const firstName = user?.firstName || "User";

  return (
    <>
      {/* Toast Notification */}
      <AnimatePresence>
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </AnimatePresence>

      <motion.div
        className="relative flex flex-col items-start w-full max-w-6xl mx-auto pt-4 pb-24 overflow-visible"
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* HEADER */}
        <div className="relative mb-10 w-full overflow-visible">
          {/* Panda avatar */}
          <div className="absolute -left-[20px] top-0 hidden sm:block">
            <div className="w-[100px] h-[100px] rounded-full bg-[#5EB1BF] flex items-center justify-center overflow-hidden">
              <img
                src={pandaAvatar}
                alt="Panda"
                className="w-[100px] h-[100px] object-contain"
              />
            </div>
          </div>

          {/* Text block — shifted right */}
          <div className="ml-[120px]">
            <h3 className="font-gotham font-medium text-[20px] leading-snug text-[#323232]">
              {firstName}, list the key stakeholders you work with or who influence your growth
            </h3>
            <p className="text-[15px] text-[#7A7A7A] mt-1 font-gotham font-normal">
              Select minimum 5 stakeholders from the drop down.
            </p>
          </div>
        </div>

        {/* INPUT ROWS */}
        <div className="w-full pl-[150px] mb-6 overflow-visible">
          <div className="grid grid-cols-[530px_240px] gap-x-8 gap-y-5">
            {stakeholders.map((s, i) => (
              <React.Fragment key={s.id}>
                {/* Left side: name + animal */}
                <div className="flex items-center border-b border-gray-300 pb-2">
                  <img
                    src={s.animal}
                    alt="Animal icon"
                    className="w-12 h-12 mr-5 object-contain"
                  />
                  <input
                    type="text"
                    placeholder={`Stakeholder ${i + 1}`}
                    value={s.name}
                    onChange={(e) => handleChange(i, "name", e.target.value)}
                    className="flex-1 bg-transparent border-none outline-none text-[18px] text-[#323232] placeholder-[#A0A0A0] focus:ring-0"
                  />
                  <button
                    onClick={() => deleteStakeholder(s.id)}
                    className="ml-2 text-gray-400 hover:text-[#ed254e] transition"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Right side: relation dropdown */}
                <div className="flex items-center">
                  <select
                    value={s.relation || ""}
                    onChange={(e) => handleChange(i, "relation", e.target.value)}
                    className="border border-[#E5E5E5] bg-white text-[#323232] text-[15px] rounded-md py-2 px-3 w-full focus:ring-2 focus:ring-[#5EB1BF] focus:outline-none"
                  >
                    <option value="">Select Relation</option>
                    {relationOptions.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Custom input row below both columns */}
                {s.relation === "Other" && (
                  <div className="col-span-2 flex flex-col items-end">
                    <input
                      type="text"
                      placeholder="Specify relation..."
                      value={s.customRelation}
                      onChange={(e) => handleCustomRelationChange(i, e.target.value)}
                      onKeyDown={(e) => handleCustomRelationKey(i, e)}
                      className="border border-[#E5E5E5] bg-white text-[#323232] text-[15px] rounded-md py-2 px-3 w-[240px] focus:ring-2 focus:ring-[#5EB1BF] focus:outline-none mt-1"
                      autoFocus
                    />
                    <p className="text-xs text-[#7A7A7A] mt-1">
                      Press <b>Enter ↵</b> to add.
                    </p>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* ADD MORE */}
          <button
            onClick={addStakeholder}
            className="text-[#54B0AF] text-[15px] font-medium flex items-center gap-1 mt-4"
          >
            <span className="text-lg leading-none">＋</span> Add more
          </button>
        </div>

        {/* NAVIGATION */}
        <div className="absolute bottom-4 right-4 flex gap-4">
          <button
            onClick={onPreviousStep}
            className="p-3 rounded-full bg-gray-100 hover:bg-gray-200 transition shadow-sm"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>

          <button
            onClick={handleNext}
            disabled={validCount < 5 || isSaving}
            className={`p-3 rounded-full shadow-sm transition ${
              validCount >= 5
                ? "bg-[#54B0AF] hover:bg-teal-600 text-white"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            {isSaving ? (
              <span className="animate-pulse text-sm px-2">Saving...</span>
            ) : (
              <ChevronRight className="w-5 h-5" />
            )}
          </button>
        </div>
      </motion.div>
    </>
  );
};

export default StakeholderBuilder;
